/**
 * `/admin/billing-providers` — pull/import flow for existing Paddle /
 * Polar catalogues (OCP-194).
 *
 * Provider tabs at the top. The table per tab shows every remote
 * product with its current OCP link state. Unlinked rows expose two
 * actions: "Link to existing OCP row" (dropdown of unlinked local
 * products for this provider) and "Create new OCP row from this".
 *
 * No top-level "import all" button by design — operator should look at
 * each remote product, decide if it belongs in OCP, and either link
 * or create explicitly. Bulk import is a future addition.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { BillingProviderStatusCard } from '../components/BillingProviderStatusCard';
import { WebhookEventsCard } from '../components/WebhookEventsCard';
import { DisputeBreakerCard } from '../components/DisputeBreakerCard';
import { api, ApiError } from '../lib/api';

type Provider = 'paddle' | 'polar';

interface RemoteRow {
  id: string;
  name: string;
  status: string;
  description: string | null;
  price_eur_cents: number;
  currency: string | null;
  billing_period: 'one-time' | 'monthly' | 'yearly';
  linked_to_local_id: string | null;
}

interface LocalProduct {
  id: string;
  display_name: string;
  product_family: string;
  paddle_product_id: string | null;
  polar_product_id: string | null;
}

const FAMILIES = ['orboto-saas', 'orboto-self-host', 'oms', 'backup', 'other'] as const;

export function AdminBillingProvidersPage() {
  const [provider, setProvider] = useState<Provider>('polar');
  const [remoteRows, setRemoteRows] = useState<RemoteRow[] | null>(null);
  const [localRows, setLocalRows] = useState<LocalProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [remote, local] = await Promise.all([
        api<{ items: RemoteRow[] }>(
          `/v1/admin/billing-providers/${provider}/products`,
        ),
        api<{ items: LocalProduct[] }>(
          `/v1/admin/products?include_inactive=true`,
        ),
      ]);
      setRemoteRows(remote.items);
      setLocalRows(local.items);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (body?.error === 'provider_not_configured') {
          setError(
            `${provider.toUpperCase()} client not configured. Set ${
              provider === 'paddle' ? 'PADDLE_API_KEY' : 'POLAR_ACCESS_TOKEN'
            } in Coolify and redeploy.`,
          );
        } else if (body?.error === 'remote_list_failed') {
          setError(`Could not reach ${provider} — see Glitchtip for upstream error.`);
        } else {
          setError(`Load failed (HTTP ${err.status}).`);
        }
      } else {
        setError('Load failed.');
      }
      setRemoteRows([]);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    void load();
  }, [load]);

  const unlinkedLocalProducts = useMemo(() => {
    return localRows.filter((p) =>
      provider === 'paddle' ? !p.paddle_product_id : !p.polar_product_id,
    );
  }, [localRows, provider]);

  async function importRow(remoteRow: RemoteRow, body: {
    mode: 'link' | 'create';
    local_product_id?: string;
    product_family?: string;
    new_local_id?: string;
  }) {
    setImportingId(remoteRow.id);
    setImportError(null);
    try {
      await api(
        `/v1/admin/billing-providers/${provider}/products/${encodeURIComponent(remoteRow.id)}/import`,
        { method: 'POST', body },
      );
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        const b = err.body as { error?: string } | null;
        setImportError(b?.error ?? `HTTP ${err.status}`);
      } else {
        setImportError('Import failed.');
      }
    } finally {
      setImportingId(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* OCP-580 - dispute circuit breaker state + reset. Sits on this page
          because it is THE payment-operations surface; the operator push
          notification links here. */}
      {/* OCP-575 - is the active provider actually wired? Shown before
          anything else on the payment-operations surface. */}
      <BillingProviderStatusCard />
      <DisputeBreakerCard />
      {/* OCP-932 - failed / abandoned provider webhook events + redrive. */}
      <WebhookEventsCard />
      <div>
        <h1 className="text-lg font-semibold">Billing providers — import</h1>
        <p className="mt-1 text-xs text-gray-500">
          Pull existing products from Paddle / Polar into your local
          /admin/products catalog. Link an existing local row to the remote
          id, or create a fresh local row from the remote shape.
        </p>
      </div>

      <div className="flex gap-2">
        {(['polar', 'paddle'] as Provider[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setProvider(p)}
            className={`rounded border px-3 py-1.5 text-sm ${
              provider === p
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            {p === 'paddle' ? 'Paddle' : 'Polar'}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Reload
        </button>
      </div>

      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {importError && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          Import failed — {importError}
        </div>
      )}

      {loading && !remoteRows && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {remoteRows && remoteRows.length === 0 && !error && (
        <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-6 text-center text-sm text-gray-500">
          No products found on {provider}.
        </div>
      )}

      {remoteRows && remoteRows.length > 0 && (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] table-auto border-collapse text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-600 dark:text-gray-400">
            <tr>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Remote ID</th>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Name</th>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Price</th>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Period</th>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Status</th>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Link state</th>
              <th className="border-b border-gray-200 dark:border-gray-800 px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {remoteRows.map((r) => (
              <ImportTableRow
                key={r.id}
                row={r}
                unlinkedLocalProducts={unlinkedLocalProducts}
                disabled={importingId !== null}
                isImporting={importingId === r.id}
                onImport={(body) => void importRow(r, body)}
              />
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function ImportTableRow({
  row,
  unlinkedLocalProducts,
  disabled,
  isImporting,
  onImport,
}: {
  row: RemoteRow;
  unlinkedLocalProducts: LocalProduct[];
  disabled: boolean;
  isImporting: boolean;
  onImport: (body: {
    mode: 'link' | 'create';
    local_product_id?: string;
    product_family?: string;
    new_local_id?: string;
  }) => void;
}) {
  const [showLink, setShowLink] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const [createFamily, setCreateFamily] = useState<string>('other');

  const priceLabel = `${(row.price_eur_cents / 100).toFixed(2)} ${row.currency ?? ''}`.trim();
  const isLinked = row.linked_to_local_id !== null;

  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{row.id}</td>
      <td className="px-3 py-2">
        <div className="font-medium">{row.name}</div>
        {row.description && (
          <div className="text-xs text-gray-500">{row.description}</div>
        )}
      </td>
      <td className="px-3 py-2 tabular-nums">{priceLabel}</td>
      <td className="px-3 py-2 text-xs">{row.billing_period}</td>
      <td className="px-3 py-2 text-xs">
        <span
          className={`rounded px-1.5 py-0.5 ${
            row.status === 'active'
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
          }`}
        >
          {row.status}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">
        {isLinked ? (
          <span className="text-gray-700 dark:text-gray-300">
            Linked → <code className="font-mono">{row.linked_to_local_id}</code>
          </span>
        ) : (
          <span className="text-gray-400">Not linked</span>
        )}
      </td>
      <td className="px-3 py-2">
        {isLinked ? (
          <span className="text-xs text-gray-400">—</span>
        ) : showLink ? (
          <div className="flex items-center gap-2">
            <select
              value={linkTarget}
              onChange={(e) => setLinkTarget(e.target.value)}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
            >
              <option value="">Select local product…</option>
              {unlinkedLocalProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id} — {p.display_name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!linkTarget || disabled}
              onClick={() => onImport({ mode: 'link', local_product_id: linkTarget })}
              className="rounded bg-brand-500 px-2 py-1 text-xs text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {isImporting ? 'Linking…' : 'Link'}
            </button>
            <button
              type="button"
              onClick={() => setShowLink(false)}
              className="text-xs text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={disabled || unlinkedLocalProducts.length === 0}
              title={
                unlinkedLocalProducts.length === 0
                  ? 'No unlinked local products available — create one first or use "Create new" here.'
                  : 'Link this remote id onto an existing OCP product row.'
              }
              onClick={() => setShowLink(true)}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Link to existing
            </button>
            <select
              value={createFamily}
              onChange={(e) => setCreateFamily(e.target.value)}
              disabled={disabled}
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs"
            >
              {FAMILIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onImport({ mode: 'create', product_family: createFamily })}
              className="rounded bg-brand-500 px-2 py-1 text-xs text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {isImporting ? 'Creating…' : 'Create new'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
